import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import BackButton from '../components/ui/BackButton';

// Partner data with descriptions
const MANUFACTURING_PARTNERS = [
  {
    id: 'coulisse',
    name: 'Coulisse',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/lzxjiv6m_image.png',
    description: 'Dutch innovation leader in window covering solutions, known for premium quality and cutting-edge motor technology.',
    href: '/partners/coulisse',
    website: 'https://coulisse.com'
  },
  {
    id: 'somfy',
    name: 'Somfy',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/5hn0d907_image.png',
    description: 'World leader in automatic controls for openings and closures, pioneering smart home integration since 1969.',
    href: '/partners/somfy',
    website: 'https://somfy.com'
  },
  {
    id: 'rollease-acmeda',
    name: 'Rollease Acmeda',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/ihjcblfi_image.png',
    description: 'Global manufacturer of window covering components, motors, and automation systems for residential and commercial applications.',
    href: '/partners/rollease-acmeda',
    website: 'https://rolleaseacmeda.com'
  },
  {
    id: 'vertilux',
    name: 'Vertilux',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/bcx7r6tw_image.png',
    description: 'Leading supplier of window treatment components and fabrics, offering innovative solutions for over 40 years.',
    href: '/partners/vertilux',
    website: 'https://vertilux.com'
  },
  {
    id: 'lutron',
    name: 'Lutron',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/ylx0vipe_image.png',
    description: 'Pioneer in lighting and shading control, known for Serena and Triathlon shade systems and seamless smart home integration.',
    href: '/partners/lutron',
    website: 'https://lutron.com'
  },
  {
    id: 'hunter-douglas',
    name: 'Hunter Douglas',
    logo: null,
    description: 'World\'s leading manufacturer of custom window coverings, offering iconic brands like Silhouette, Duette, and Pirouette.',
    href: '/partners/hunter-douglas',
    website: 'https://hunterdouglas.com'
  },
  {
    id: 'fabricut',
    name: 'Fabricut',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/19oc58sp_image.png',
    description: 'Premier fabric house offering extensive collections of designer fabrics, trims, and drapery hardware for luxury interiors.',
    href: '/partners/fabricut',
    website: 'https://fabricut.com'
  },
];

const SMART_PLATFORMS = [
  {
    id: 'control4',
    name: 'Control4',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/wr9zqqt9_C4_Logo_FC.png',
    description: 'Leading smart home automation platform offering seamless integration of lighting, shading, audio, video, and security.',
    href: '/partners/control4',
    website: 'https://control4.com'
  },
  {
    id: 'lutron-homeworks',
    name: 'Lutron HomeWorks',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/ylx0vipe_image.png',
    description: 'Premium whole-home lighting and shade control system, offered through our Lutron partnership with compatibility confirmed for each project.',
    href: '/partners/lutron-homeworks',
    website: 'https://lutron.com/homeworks'
  },
];

const PartnerCard = ({ partner, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    className="group"
  >
    <Link to={partner.href} className="block">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100 hover:shadow-xl hover:border-amber-200 transition-all duration-300 h-full">
        {/* Logo Area - Fixed aspect ratio container */}
        <div className="relative aspect-[3/2] mb-5 overflow-hidden rounded-lg bg-neutral-50 flex items-center justify-center group-hover:bg-amber-50/50 transition-colors p-6">
          {partner.logo ? (
            <img 
              src={partner.logo} 
              alt={`${partner.name} logo`}
              className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform"
              style={{ maxHeight: '80px' }}
            />
          ) : (
            <span className="font-serif text-2xl text-neutral-500 text-center" data-testid={`partner-wordmark-${partner.id}`}>{partner.name}</span>
          )}
          {/* Hover overlay effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/0 via-amber-500/10 to-amber-500/0 opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-[-100%] group-hover:translate-x-[100%] duration-1000" />
        </div>
        
        {/* Content */}
        <h3 className="text-lg font-semibold text-neutral-900 mb-2 group-hover:text-amber-700 transition-colors">
          {partner.name}
        </h3>
        <p className="text-neutral-600 text-sm leading-relaxed mb-4 line-clamp-3">
          {partner.description}
        </p>
        
        {/* Link indicator */}
        <div className="flex items-center gap-2 text-amber-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          <span>Learn More</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  </motion.div>
);

export const PartnersPage = () => {
  return (
    <Layout>
      <div className="bg-neutral-50">
        {/* Hero Section */}
        <section className="relative py-32 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.05)_25%,rgba(255,255,255,0.05)_50%,transparent_50%,transparent_75%,rgba(255,255,255,0.05)_75%)] bg-[length:60px_60px]" />
        </div>
        
        <div className="container-xl relative">
          <BackButton variant="light" />
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center mt-8"
          >
            <span className="inline-block px-4 py-2 bg-amber-500/10 text-amber-400 text-sm font-medium rounded-full mb-6">
              Industry Leaders
            </span>
            <h1 className="text-5xl md:text-6xl font-serif text-white mb-6">
              Our <span className="text-amber-400">Partners</span>
            </h1>
            <p className="text-xl text-white/70 leading-relaxed">
              Apollo Shades works with leading manufacturers and technology providers to deliver quality window treatments and smart home integration.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Manufacturing Partners Section */}
      <section className="py-24">
        <div className="container-xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-serif text-neutral-900 mb-4">
              Partners & Featured Brands
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Apollo Shades is an authorized Lutron and Somfy dealer and works with Coulisse. The other manufacturers
              shown are featured brands; availability of their products through Apollo is confirmed for each project.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {MANUFACTURING_PARTNERS.map((partner, idx) => (
              <PartnerCard key={partner.id} partner={partner} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* Smart Platforms Section */}
      <section className="py-24 bg-white">
        <div className="container-xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-neutral-100 text-neutral-600 text-sm font-medium rounded-full mb-4">
              Home Automation
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-neutral-900 mb-4">
              Smart Platforms
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Our motorized shades integrate seamlessly with the world's leading 
              smart home automation systems for unified control.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {SMART_PLATFORMS.map((partner, idx) => (
              <PartnerCard key={partner.id} partner={partner} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-amber-50">
        <div className="container-xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-serif text-neutral-900 mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-neutral-600 mb-8">
              Our team can help you choose the perfect combination of products and 
              smart home integration for your space.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/consultation" 
                className="btn-primary px-8 py-4 text-lg"
              >
                Schedule a Consultation
              </Link>
              <Link 
                to="/quote" 
                className="btn-outline px-8 py-4 text-lg"
              >
                Request a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </Layout>
  );
};

export default PartnersPage;
